import { FC, FocusEventHandler } from 'react';
import PhoneInput, { DefaultInputComponentProps, Value, isPossiblePhoneNumber } from 'react-phone-number-input';
import { styled } from '@mui/material';

import 'react-phone-number-input/style.css';
import './input.scss';

import { RED, STROKE, TEXT } from '../../Helpers/colors';
import Text from '../Text';
import { InputBackgroundType, backgroundColors, disabledBackgroundColors } from '.';

interface PhoneInputProperties extends DefaultInputComponentProps {
	backgroundType?: InputBackgroundType;
	className?: string;
	error?: boolean;
	errorMessage?: string;
	label?: string;
	onBlur?: FocusEventHandler<HTMLInputElement>;
	onChange: (value: Value) => void;
	value: Value;
}

const PhoneInputStyled = styled(PhoneInput, {
	shouldForwardProp: (propName) => propName !== 'backgroundType',
})<PhoneInputProperties>(({ error, disabled, backgroundType = 'primary' }) => ({
	width: '100%',
	height: '40px',
	padding: '0 20px',
	backgroundColor: disabled ? disabledBackgroundColors[backgroundType] : backgroundColors[backgroundType],
	border: `1px solid ${error ? RED.BASE : STROKE.SECONDARY}`,
	borderRadius: '9px !important',
	textTransform: 'none',
	display: 'inline-flex !important',
	fontFamily: 'Inter',
	select: {
		width: '100%',
		textTransform: 'none',
		backgroundColor: disabled ? disabledBackgroundColors[backgroundType] : backgroundColors[backgroundType],
		color: TEXT.PRIMARY,
		borderColor: STROKE.SECONDARY,
		height: '40px',
		padding: '0 20px',
		fontFamily: 'Inter',
	},
	input: {
		border: 'none',
		fontFamily: 'Inter',
		color: TEXT.PRIMARY,
		'&:focus-visible': {
			outline: 'none',
		},
		' &:: placeholder': {
			color: STROKE.SECONDARY,
		},
	},
}));

const CustomPhoneInput: FC<PhoneInputProperties> = ({
	className,
	label,
	onChange,
	value,
	errorMessage = '',
	onBlur,
	backgroundType,
	disabled,
}) => (
	<div className={className}>
		<Text
			className={`inputLabel ${label ? 'active' : ''}`}
			title={label ?? ''}
			type="psm"
			fontWeight="medium"
			color={disabled ? TEXT.QUATERNARY : TEXT.PRIMARY}
		/>
		<PhoneInputStyled
			defaultCountry="US"
			onChange={onChange}
			value={value}
			maxLength={value && isPossiblePhoneNumber(value) ? value.length : 20}
			onBlur={onBlur}
			backgroundType={backgroundType}
			disabled={disabled}
		/>
		<Text
			className={`error ${errorMessage ? 'active paddingTop8' : ''}`}
			title={errorMessage}
			type="psm"
			color={RED.DARK[16]}
		/>
	</div>
);

export default CustomPhoneInput;
